import { ApiOutlet, ApiRetailer } from '@vend/types'
import { FEATURE_CROSS_LOCATION_FULFILMENT } from '../constants/wr-features'
import { getInventory } from '../components/product/products-resource/get-inventory'
import { getFeatureFromCache } from './feature-cache'

export const CLF_ACCOUNT_ATTRIBUTE = 'cross_location_fulfilment_enabled'
export const CLF_SOURCE_OUTLET_ATTRIBUTE =
  'allow_cashiers_to_fulfil_from_elsewhere'
export const CLF_TARGET_OUTLET_ATTRIBUTE =
  'allow_other_outlets_to_reserve_stock'

type ApiRetailerWithCrossLocationFulfilment = ApiRetailer & {
  [CLF_ACCOUNT_ATTRIBUTE]?: boolean | string
}

type OutletAttribute = {
  key?: string
  name?: string
  value?: unknown
}

// ApiOutlet -> ApiOutletAttribute only includes {name, value} but the API response says {key, value}
// So we need to use a type that includes key for the attribute functions below
type OutletWithAttributes = Omit<ApiOutlet, 'attributes'> & {
  attributes: OutletAttribute[]
}

const getOutletAttributes = (
  outlet?: OutletWithAttributes
): OutletAttribute[] | undefined => {
  if (!Array.isArray(outlet?.attributes)) {
    return undefined
  }

  return outlet.attributes
}

export const getOutletAttributeValue = (
  outlet: OutletWithAttributes,
  requestedAttribute: string
): string | undefined => {
  const attribute = getOutletAttributes(outlet)?.find(
    attribute =>
      attribute.key === requestedAttribute ||
      attribute.name === requestedAttribute
  )

  if (attribute?.value === null || attribute?.value === undefined) {
    return undefined
  }

  return String(attribute.value)
}

export const isCrossLocationFulfilmentRolloutEnabled = (): boolean =>
  Boolean(getFeatureFromCache(FEATURE_CROSS_LOCATION_FULFILMENT))

export const isCrossLocationFulfilmentAccountEnabled = (
  retailerSettings?: ApiRetailerWithCrossLocationFulfilment
): boolean => {
  const value = retailerSettings?.[CLF_ACCOUNT_ATTRIBUTE]

  return value === true || value === 'true'
}

export const isSourceOutletCrossLocationFulfilmentEnabled = (
  outlet?: OutletWithAttributes
): boolean =>
  !!outlet &&
  getOutletAttributeValue(outlet, CLF_SOURCE_OUTLET_ATTRIBUTE) !== 'false'

export const isTargetOutletCrossLocationFulfilmentEligible = (
  outlet?: OutletWithAttributes
): boolean =>
  !!outlet &&
  getOutletAttributeValue(outlet, CLF_TARGET_OUTLET_ATTRIBUTE) !== 'false'

export const filterEligibleCrossLocationFulfilmentTargetOutlets = <
  TOutlet extends ApiOutlet
>(
  outlets: TOutlet[],
  alwaysIncludedOutletId?: string
): TOutlet[] =>
  outlets.filter(
    outlet =>
      (alwaysIncludedOutletId && outlet.id === alwaysIncludedOutletId) ||
      isTargetOutletCrossLocationFulfilmentEligible(outlet)
  )

const availabilityCache = new Map<
  string,
  Promise<Map<string, number> | undefined>
>()
const resolvedAvailabilityCache = new Map<string, number>()
let availabilityCacheSaleLocalId: string | undefined

export const fetchAvailabilityByOutletId = async (
  productId: string,
  outletIds: string[]
) => {
  try {
    const inventoryLevels = await getInventory([productId], {
      include_variants: false,
      location_ids: outletIds,
    })

    return new Map(
      inventoryLevels
        .filter(inventoryLevel => inventoryLevel.product_id === productId)
        .map(inventoryLevel => [
          inventoryLevel.location_id,
          inventoryLevel.current_inventory_level,
        ])
    )
  } catch {
    return undefined
  }
}

// Sort a copy for the key only — the request keeps the eligible-outlet order.
const getAvailabilityCacheKey = (productId: string, outletIds: string[]) =>
  `${productId}|${[...outletIds].sort().join(',')}`

const getResolvedAvailabilityCacheKey = (productId: string, outletId: string) =>
  `${productId}|${outletId}`

/** Test hook: reset the availability cache between jest tests. */
export const clearAvailabilityCache = () => {
  availabilityCache.clear()
  resolvedAvailabilityCache.clear()
  availabilityCacheSaleLocalId = undefined
}

const resetAvailabilityCacheForSale = (saleLocalId: string) => {
  if (availabilityCacheSaleLocalId === saleLocalId) {
    return
  }

  availabilityCache.clear()
  resolvedAvailabilityCache.clear()
  availabilityCacheSaleLocalId = saleLocalId
}

export const getCachedAvailability = (
  productId: string,
  outletId: string,
  saleLocalId?: string
): number | undefined =>
  saleLocalId && availabilityCacheSaleLocalId === saleLocalId
    ? resolvedAvailabilityCache.get(
        getResolvedAvailabilityCacheKey(productId, outletId)
      )
    : undefined

// Availability is fetched once per product and outlet set, then cached for the
// active sale; concurrent and repeated mounts share the same request.
// Refreshing availability before payment is handled separately.
export const getAvailabilityByOutletId = (
  productId: string,
  outletIds: string[],
  saleLocalId?: string
): Promise<Map<string, number> | undefined> => {
  if (!saleLocalId) {
    return fetchAvailabilityByOutletId(productId, outletIds)
  }

  resetAvailabilityCacheForSale(saleLocalId)

  const key = getAvailabilityCacheKey(productId, outletIds)
  const cached = availabilityCache.get(key)

  if (cached) {
    return cached
  }

  const promise = fetchAvailabilityByOutletId(productId, outletIds).then(
    result => {
      // A failed fetch resolves undefined — don't cache failures. Identity
      // check so a newer entry for the same key is never evicted.
      if (result === undefined && availabilityCache.get(key) === promise) {
        availabilityCache.delete(key)
      }

      if (
        result &&
        availabilityCacheSaleLocalId === saleLocalId &&
        availabilityCache.get(key) === promise
      ) {
        outletIds.forEach(outletId => {
          resolvedAvailabilityCache.set(
            getResolvedAvailabilityCacheKey(productId, outletId),
            result.get(outletId) ?? 0
          )
        })
      }
      return result
    }
  )

  availabilityCache.set(key, promise)
  return promise
}
